import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { CanUpdateErrorState, ErrorStateMatcher, mixinErrorState } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { ControlValueAccessor, UntypedFormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  DoCheck,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  SimpleChanges,
  ViewEncapsulation,
  forwardRef,
} from '@angular/core';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ScheduleInputValue, ScheduleInputValuePriority } from '../../types';
import { provideTranslation } from '@skyeng/libs/shared/i18n';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { TeacherSelectionUnavailableSlots } from '../../../../../teachers-schedule/person/types';
import { EServiceTypeKey } from '@skyeng/libs/shared/stk';

class MatInputBase {
  constructor(
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    /** @docs-private */
    public ngControl: NgControl,
    public stateChanges: Subject<void>,
  ) {}
}

export const _MatInputMixinBase = mixinErrorState(MatInputBase);

interface UnavailableSlotsByDayTime {
  [key: number]: {
    [key: number]: {
      type: string;
      serviceTypeKey: EServiceTypeKey;
      dateFrom: string;
      dateTo: string;
    }[];
  };
}

@DecorateUntilDestroy()
@Component({
  selector: 'crm-schedule-mat-input',
  templateUrl: './schedule-mat-input.component.html',
  styleUrls: ['./schedule-mat-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: forwardRef(() => ScheduleMatInputComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideTranslation('formFieldSchedule', () => import.meta.webpackContext!('./translation'))],
})
export class ScheduleMatInputComponent
  extends _MatInputMixinBase
  implements
    CanUpdateErrorState,
    ControlValueAccessor,
    MatFormFieldControl<ScheduleInputValue[]>,
    OnChanges,
    OnInit,
    DoCheck,
    OnDestroy
{
  @Input() teacherSelectionUnavailableSlots: TeacherSelectionUnavailableSlots = {};

  get value(): ScheduleInputValue[] {
    return this.editorControl.value;
  }

  set value(newValue: ScheduleInputValue[]) {
    this.writeValue(newValue);
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }

  set placeholder(newPlaceholder: string) {
    this._placeholder = newPlaceholder;
    this.stateChanges.next();
  }

  get empty(): boolean {
    return !!this.editorControl.value;
  }

  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(isRequired: boolean) {
    this._required = coerceBooleanProperty(isRequired);
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(isDisabled: boolean) {
    this.setDisabledState(isDisabled);
  }

  @Input() minutesStep = 60;

  @HostBinding() id = `crm-schedule-mat-input-0`;
  @HostBinding('attr.aria-describedby') describedBy = '';

  slots: number[] = [];
  daysOfWeekTranslateKeys = [1, 2, 3, 4, 5, 6, 0];

  readonly indexPerDayTranslateKey = {
    0: 'sunday',
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
  };

  selection = false;

  priority: ScheduleInputValuePriority = 'normal';

  _toggledSlotsMap = new Map<number, Map<number, ScheduleInputValuePriority>>();
  private _placeholder: string;
  focused = false;
  private _required = false;
  private _disabled = false;
  shouldLabelFloat = true;
  readonly controlType: string = 'crm-schedule-input';
  editorControl: UntypedFormControl = new UntypedFormControl([]);

  unavailableSlotsByDayTime: UnavailableSlotsByDayTime = {};

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    private _focusMonitor: FocusMonitor,
    private _elRef: ElementRef,
  ) {
    super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl, new Subject<void>());

    if (ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
  }

  @HostListener('mouseleave')
  onLeave(): void {
    this.selection = false;
  }

  @HostListener('mouseup')
  onUp(): void {
    this.selection = false;
  }

  // eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
  ngOnInit(): void {
    for (let nextSlotsInMin = 0; nextSlotsInMin < 24 * 60; nextSlotsInMin += this.minutesStep) {
      this.slots.push(nextSlotsInMin);
    }

    this._focusMonitor
      .monitor(this._elRef.nativeElement, true)
      .pipe(takeUntilDestroyed(this))
      .subscribe((origin: string) => {
        this.focused = !!origin;

        if (!this.focused) {
          this._onTouched();
        }

        this.stateChanges.next();
      });

    this.editorControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter(() => this.editorControl.enabled),
        takeUntilDestroyed(this),
      )
      .subscribe((newValue: string) => {
        this._onChange(newValue);
      });
  }

  // eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
  ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  // eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
  ngOnChanges(changes: SimpleChanges): void {
    this.stateChanges.next();

    if (changes.teacherSelectionUnavailableSlots?.currentValue) {
      this.unavailableSlotsByDayTime = this.mapTeacherSelectionUnavailableSlots(this.teacherSelectionUnavailableSlots);
    }
  }

  // eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
  ngOnDestroy(): void {
    this.stateChanges.complete();

    this._focusMonitor.stopMonitoring(this._elRef.nativeElement);
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(_event: MouseEvent): void {}

  writeValue(newValue: ScheduleInputValue[]): void {
    let setValue = newValue;
    if (!Array.isArray(newValue)) {
      setValue = [];
    }

    this.editorControl.setValue(setValue);
    this._toggledSlotsMap.clear();
    setValue.forEach(it => {
      const minutes = it.time * 60 + (it.minutes || 0);
      if (this._toggledSlotsMap.get(it.day)) {
        const dayEntry = this._toggledSlotsMap.get(it.day);
        dayEntry.set(minutes, it.priority);
      } else {
        this._toggledSlotsMap.set(it.day, new Map<number, ScheduleInputValuePriority>([[minutes, it.priority]]));
      }
    });
    this.stateChanges.next();
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = coerceBooleanProperty(isDisabled);

    if (this._disabled) {
      this.editorControl.disable();
    } else {
      this.editorControl.enable();
    }

    this.stateChanges.next();
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this._onTouched = fn;
  }

  toggle(day: number, minutes: number): void {
    this.ngControl.control.markAsTouched();

    const minutesRemains = minutes % 60;
    const hours = (minutes - minutesRemains) / 60;

    const value = this.editorControl.value as ScheduleInputValue[];
    const existingIndex = value.findIndex(
      val => val.day === day && val.time === hours && (val.minutes || 0) === minutesRemains,
    );
    const hasCurrentDayTime = existingIndex !== -1;

    if (!hasCurrentDayTime) {
      return this.writeValue([...value, { day: day, time: hours, minutes: minutesRemains, priority: this.priority }]);
    }

    const existingValue = value[existingIndex];
    if (existingValue.priority === this.priority) {
      return this.writeValue(value.filter((_, index) => index !== existingIndex));
    }

    value[existingIndex].priority = this.priority;

    this.writeValue([...value]);
  }

  onHover(day: number, time: number): void {
    if (this.selection) {
      this.toggle(day, time);
    }
  }

  onDown(day: number, time: number): void {
    this.selection = true;
    this.toggle(day, time);
  }

  private _onChange: (value: string) => void = () => {};

  private _onTouched = () => {};

  priorityChange(change: MatButtonToggleChange): void {
    this.priority = change.value;
  }

  private mapTeacherSelectionUnavailableSlots(slots: TeacherSelectionUnavailableSlots): UnavailableSlotsByDayTime {
    const slotsMap: UnavailableSlotsByDayTime = {};

    const convertMinutesToHours24 = (minutes: number): number => Math.floor(minutes / 60) % 24;

    for (const key in slots) {
      for (const record of slots[key]) {
        const timeMsk = record.time + 3;

        const overflowedDays = Math.floor(timeMsk / 24);
        const dayOfWeekStartedFromSunday = record.day === 6 ? 0 : record.day + 1;
        const day = (dayOfWeekStartedFromSunday + overflowedDays) % 7;

        const startTimeMinutes = (timeMsk * 60 + record.minutes) % (60 * 24);

        const startHours = `${convertMinutesToHours24(startTimeMinutes).toString().padStart(2, '0')}`;
        const startMinutes = `${(startTimeMinutes % 60).toString().padStart(2, '0')}`;

        const durationMinutes = Math.floor(record.minutes + record.durationSeconds / 60);
        const endHours = `${convertMinutesToHours24(startTimeMinutes + durationMinutes)}`.padStart(2, '0');
        const endMinutes = `${(startTimeMinutes + durationMinutes) % 60}`.padStart(2, '0');

        // eslint-disable-next-line functional/immutable-data
        slotsMap[day] ||= {};
        // eslint-disable-next-line functional/immutable-data
        slotsMap[day][startTimeMinutes] ||= [];
        // eslint-disable-next-line functional/immutable-data
        slotsMap[day][startTimeMinutes].push({
          type: key,
          serviceTypeKey: record.serviceTypeKey,
          dateFrom: `${startHours}:${startMinutes}`,
          dateTo: `${endHours}:${endMinutes}`,
        });
      }
    }

    return slotsMap;
  }
}
